/**
 * services/sync/LocalDirectorySyncProvider.ts
 * File System Access API implementation of SyncProvider
 *
 * Enables sync to a local directory chosen by the user. Uses the browser's
 * File System Access API (Chrome/Edge only) to read/write sync files with
 * persistent permissions stored in IndexedDB.
 */

import { db } from '@/lib/db';
import { generateUUID } from '@/utils/uuid';
import type {
  SyncProvider,
  SyncProviderType,
  SyncFileInfo,
  SyncError,
  SyncErrorCode,
} from '@/types/sync/SyncProvider';
import { SyncProviderEnum } from '@/types/sync/SyncProvider';

/**
 * Storage key for directory handle in IndexedDB
 */
const DIRECTORY_HANDLE_ID = 'local_sync_directory';

/**
 * Local directory sync provider using File System Access API
 *
 * Provides seamless sync to a user-selected local directory with permission
 * persistence across browser sessions. Supports all CRUD operations on sync files.
 *
 * **Browser Support**: Chrome 86+, Edge 86+ (File System Access API required)
 *
 * @example
 * ```typescript
 * const provider = new LocalDirectorySyncProvider();
 *
 * // First time: Show directory picker
 * await provider.connect();
 *
 * // Subsequent sessions: Auto-reconnect with stored handle
 * if (await provider.isConnected()) {
 *   await provider.write('manifest.json', JSON.stringify(manifest));
 * }
 * ```
 */
export class LocalDirectorySyncProvider implements SyncProvider {
  /** Provider identifier */
  readonly name: SyncProviderType = SyncProviderEnum.Local as SyncProviderType;

  /** Indicates this provider supports binary file operations */
  readonly supportsBinary = true;

  /** Cached directory handle (loaded from IndexedDB or picker) */
  private directoryHandle: FileSystemDirectoryHandle | null = null;

  /** DEV MODE: Track if mock connection has been established (E2E testing) */
  private mockConnected: boolean = false;

  /**
   * Connect to local directory via File System Access API
   *
   * Shows native directory picker on first connection, then stores handle
   * in IndexedDB for future sessions. If already connected, verifies
   * existing handle and permissions.
   *
   * @param options - Optional connection options
   *   - forceNewSelection: Force showing the picker even if already connected (default: false)
   *   - promptMode: Ignored by LocalDirectorySyncProvider (cloud-only option)
   * @returns Promise resolving to true if connection successful
   * @throws Never throws - returns false on any error
   */
  async connect(options?: import('@/types/sync/SyncProvider').ProviderConnectOptions): Promise<boolean> {
    const forceNew = !!options?.forceNewSelection;

    // DEV MODE: Mock successful connection for E2E testing
    // Allows testing reconnection flows without File System API picker
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const mockSuccess = params.get('mockLocalDirectorySuccess');

      if (mockSuccess === 'true') {
        console.log('[LocalDirectorySyncProvider] DEV MODE: Mocking successful connection');
        // Set flag so isConnected() returns true after this point (instance and global)
        this.mockConnected = true;
        // Persist mock state globally so new instances also see it
        sessionStorage.setItem('__test_local_directory_mock_connected', 'true');
        // Simulate successful connection without picker
        // Note: directoryHandle remains null, but for E2E testing of UI flows, this is acceptable
        return true;
      }
    }

    try {
      // Check browser support
      if (!('showDirectoryPicker' in window)) {
        console.error(
          'File System Access API not supported. Use Chrome/Edge for local sync.'
        );
        return false;
      }

      // Try to load existing handle from IndexedDB (unless forcing new)
      let existingHandle: FileSystemDirectoryHandle | null = null;

      if (!forceNew) {
        const stored = await db.directory_handles.get(DIRECTORY_HANDLE_ID);

        if (stored?.handle) {
          // Verify permission on stored handle
          const permission = await this.verifyPermission(stored.handle);

          if (permission) {
            this.directoryHandle = stored.handle;
            console.log('✅ Reconnected to stored directory');
            return true;
          }

          console.log('⚠️ Stored directory permission expired, requesting new directory');
        }
      } else {
        console.log('🔄 Forcing new directory selection');

        // Load existing handle to use as startIn hint for picker
        const stored = await db.directory_handles.get(DIRECTORY_HANDLE_ID);
        if (stored?.handle) {
          existingHandle = stored.handle;
        }
      }

      // Show directory picker (user gesture required)
      // Type assertion needed: File System Access API not in standard TS lib
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const picker = (window as any).showDirectoryPicker;

      // If we have an existing handle (when changing directory), use it as startIn
      // This opens the picker to the current directory, making it easier to navigate
      this.directoryHandle = await picker({
        mode: 'readwrite',
        startIn: existingHandle || 'documents',
      });

      // Store handle in IndexedDB for persistence
      await db.directory_handles.put({
        id: DIRECTORY_HANDLE_ID,
        provider: this.name,
        handle: this.directoryHandle as FileSystemDirectoryHandle,
        createdAt: new Date().toISOString(),
      });

      console.log('✅ Connected to new directory');
      return true;
    } catch (error) {
      // User cancelled picker or permission denied
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.log('ℹ️ Directory picker cancelled by user');
        } else {
          console.error('❌ Failed to connect to directory:', error);
        }
      }

      return false;
    }
  }

  /**
   * Check if provider is currently connected and has permissions
   *
   * Verifies that a directory handle exists and still has valid read/write
   * permissions. Permissions can expire and need re-verification.
   *
   * @returns Promise resolving to true if connected with valid permissions
   */
  async isConnected(): Promise<boolean> {
    // DEV MODE: Mock successful connection check for E2E testing
    // Check both instance flag and global sessionStorage for mock state
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const mockSuccess = params.get('mockLocalDirectorySuccess');
      const globalMockConnected = sessionStorage.getItem('__test_local_directory_mock_connected');

      if (mockSuccess === 'true' && (this.mockConnected || globalMockConnected === 'true')) {
        console.log('[LocalDirectorySyncProvider] DEV MODE: Mocking isConnected() = true');
        return true;
      }
    }

    try {
      // Check if handle exists
      if (!this.directoryHandle) {
        // Try to load from IndexedDB
        const stored = await db.directory_handles.get(DIRECTORY_HANDLE_ID);

        if (!stored?.handle) {
          return false;
        }

        this.directoryHandle = stored.handle;
      }

      // Verify permission
      return await this.verifyPermission(this.directoryHandle);
    } catch (_error) {
      return false;
    }
  }

  /**
   * Get the name of the connected directory
   *
   * @returns Promise resolving to directory name, or null if not connected
   */
  async getDirectoryName(): Promise<string | null> {
    try {
      // Check if handle exists
      if (!this.directoryHandle) {
        // Try to load from IndexedDB
        const stored = await db.directory_handles.get(DIRECTORY_HANDLE_ID);

        if (!stored?.handle) {
          return null;
        }

        this.directoryHandle = stored.handle;
      }

      // Return the directory name
      return this.directoryHandle.name;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Read file contents from directory
   *
   * @param filename - Name of file to read
   * @returns Promise resolving to file contents as string
   * @throws Error if not connected, file not found, or permission denied
   */
  async read(filename: string): Promise<string> {
    const handle = await this.ensureDirectoryHandle();

    try {
      const fileHandle = await handle.getFileHandle(filename);
      const file = await fileHandle.getFile();
      return await file.text();
    } catch (error) {
      throw this.createSyncError(
        error,
        'FILE_NOT_FOUND',
        `Failed to read file: ${filename}`
      );
    }
  }

  /**
   * Write file contents to directory
   *
   * Creates new file if it doesn't exist, overwrites if it does.
   *
   * @param filename - Name of file to write
   * @param data - File contents as string (typically JSON)
   * @returns Promise resolving when write completes
   * @throws Error if not connected, permission denied, or quota exceeded
   */
  async write(filename: string, data: string): Promise<void> {
    const handle = await this.ensureDirectoryHandle();

    try {
      const fileHandle = await handle.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();

      await writable.write(data);
      await writable.close();
    } catch (error) {
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        `Failed to write file: ${filename}`
      );
    }
  }

  /**
   * List all sync files in directory
   *
   * Returns metadata for all `.json` files in the sync directory
   * (baselines, deltas, and manifest).
   *
   * @returns Promise resolving to array of file metadata
   * @throws Error if not connected or permission denied
   */
  async list(): Promise<SyncFileInfo[]> {
    const handle = await this.ensureDirectoryHandle();

    try {
      const files: SyncFileInfo[] = [];

      // Iterate through directory entries
      // Type assertion needed: File System Access API not in standard TS lib
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for await (const entry of (handle as any).values()) {
        // Only include JSON files (sync files)
        if (entry.kind === 'file' && entry.name.endsWith('.json')) {
          const fileHandle = await handle.getFileHandle(entry.name);
          const file = await fileHandle.getFile();

          files.push({
            filename: entry.name,
            size: file.size,
            lastModified: new Date(file.lastModified).toISOString(),
          });
        }
      }

      return files;
    } catch (error) {
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        'Failed to list directory files'
      );
    }
  }

  /**
   * Delete file from directory
   *
   * @param filename - Name of file to delete
   * @returns Promise resolving when deletion completes
   * @throws Error if not connected, file not found, or permission denied
   */
  async delete(filename: string): Promise<void> {
    const handle = await this.ensureDirectoryHandle();

    try {
      await handle.removeEntry(filename);
    } catch (error) {
      throw this.createSyncError(
        error,
        'FILE_NOT_FOUND',
        `Failed to delete file: ${filename}`
      );
    }
  }

  /**
   * Disconnect from provider and cleanup resources
   *
   * Clears both cached directory handle and stored handle in IndexedDB.
   * User will need to re-select directory and grant permissions to reconnect.
   *
   * @returns Promise resolving when disconnect completes
   */
  async disconnect(): Promise<void> {
    // Clear in-memory handle
    this.directoryHandle = null;

    // Remove stored handle from IndexedDB (hard disconnect)
    try {
      await db.directory_handles.delete(DIRECTORY_HANDLE_ID);
      console.log('✅ Disconnected from local directory (handle removed from IndexedDB)');
    } catch (error) {
      console.error('⚠️ Failed to remove directory handle from IndexedDB:', error);
      throw error;
    }
  }

  // ===== BINARY FILE OPERATIONS =====

  /**
   * Read binary file contents from images subdirectory
   *
   * @param filename - Name of file to read (e.g., 'abc123.webp')
   * @returns Promise resolving to file contents as ArrayBuffer
   * @throws SyncError with FILE_NOT_FOUND if file or images folder doesn't exist
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   */
  async readBinary(filename: string): Promise<ArrayBuffer> {
    const rootHandle = await this.ensureDirectoryHandle();

    try {
      const imagesHandle = await rootHandle.getDirectoryHandle('images', { create: false });
      const fileHandle = await imagesHandle.getFileHandle(filename);
      const file = await fileHandle.getFile();
      return await file.arrayBuffer();
    } catch (error) {
      throw this.createSyncError(
        error,
        'FILE_NOT_FOUND',
        `Failed to read binary file: ${filename}`
      );
    }
  }

  /**
   * Write binary file contents to images subdirectory
   *
   * Creates the images subdirectory if it doesn't exist.
   * Creates new file if it doesn't exist, overwrites if it does.
   *
   * @param filename - Name of file to write (e.g., 'abc123.webp')
   * @param data - File contents as ArrayBuffer
   * @param _mimeType - Optional MIME type hint (ignored by local provider)
   * @returns Promise resolving when write completes
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   * @throws SyncError with QUOTA_EXCEEDED if storage limit reached
   */
  async writeBinary(filename: string, data: ArrayBuffer, _mimeType?: string): Promise<void> {
    const rootHandle = await this.ensureDirectoryHandle();

    try {
      const imagesHandle = await rootHandle.getDirectoryHandle('images', { create: true });
      const fileHandle = await imagesHandle.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();

      await writable.write(data);
      await writable.close();
    } catch (error) {
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        `Failed to write binary file: ${filename}`
      );
    }
  }

  /**
   * Delete binary file from images subdirectory
   *
   * Idempotent: returns silently if file or images folder doesn't exist.
   *
   * @param filename - Name of file to delete (e.g., 'abc123.webp')
   * @returns Promise resolving when deletion completes
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   */
  async deleteBinary(filename: string): Promise<void> {
    const rootHandle = await this.ensureDirectoryHandle();

    try {
      const imagesHandle = await rootHandle.getDirectoryHandle('images', { create: false });
      await imagesHandle.removeEntry(filename);
    } catch (error) {
      // Idempotent: if folder or file doesn't exist, return silently
      if (error instanceof Error && error.name === 'NotFoundError') {
        return;
      }
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        `Failed to delete binary file: ${filename}`
      );
    }
  }

  /**
   * Check if binary file exists in images subdirectory
   *
   * @param filename - Name of file to check (e.g., 'abc123.webp')
   * @returns Promise resolving to true if file exists, false otherwise
   * @throws SyncError with NOT_CONNECTED if not connected
   * @throws SyncError with PERMISSION_DENIED if access denied
   */
  async existsBinary(filename: string): Promise<boolean> {
    const rootHandle = await this.ensureDirectoryHandle();

    try {
      const imagesHandle = await rootHandle.getDirectoryHandle('images', { create: false });
      await imagesHandle.getFileHandle(filename);
      return true;
    } catch (error) {
      // If folder or file doesn't exist, return false
      if (error instanceof Error && error.name === 'NotFoundError') {
        return false;
      }
      throw this.createSyncError(
        error,
        'UNKNOWN_ERROR',
        `Failed to check binary file existence: ${filename}`
      );
    }
  }

  // ===== PRIVATE HELPER METHODS =====

  /**
   * Ensure directory handle is available and has permissions
   *
   * @returns Promise resolving to valid directory handle
   * @throws Error if not connected or permission denied
   */
  private async ensureDirectoryHandle(): Promise<FileSystemDirectoryHandle> {
    if (!this.directoryHandle) {
      // Try to load from IndexedDB
      const stored = await db.directory_handles.get(DIRECTORY_HANDLE_ID);

      if (stored?.handle) {
        this.directoryHandle = stored.handle;
      }
    }

    if (!this.directoryHandle) {
      throw this.createSyncError(
        new Error('Not connected'),
        'NOT_CONNECTED',
        'No directory selected. Call connect() first.'
      );
    }

    // Verify permission
    const hasPermission = await this.verifyPermission(this.directoryHandle);

    if (!hasPermission) {
      throw this.createSyncError(
        new Error('Permission denied'),
        'PERMISSION_DENIED',
        'Directory access permission denied. Call connect() to re-authorize.'
      );
    }

    return this.directoryHandle;
  }

  /**
   * Verify and request permission for directory handle
   *
   * Checks if we have read/write permission for the handle. If permission
   * is "prompt", requests it from the user. This handles permission expiration
   * gracefully without re-showing the directory picker.
   *
   * @param handle - Directory handle to verify
   * @returns Promise resolving to true if permission granted
   */
  private async verifyPermission(
    handle: FileSystemDirectoryHandle
  ): Promise<boolean> {
    try {
      // Type assertion needed: File System Access API not in standard TS lib
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handleWithPerms = handle as any;

      // Check current permission status
      const permission = await handleWithPerms.queryPermission({ mode: 'readwrite' });

      if (permission === 'granted') {
        return true;
      }

      // Permission is "prompt" - request it
      const requestResult = await handleWithPerms.requestPermission({ mode: 'readwrite' });

      return requestResult === 'granted';
    } catch (_error) {
      return false;
    }
  }

  /**
   * Create structured SyncError from native error
   *
   * Maps File System API errors to SyncErrorCode for consistent error handling.
   *
   * @param error - Native error from File System API
   * @param fallbackCode - Default error code if mapping fails
   * @param message - Human-readable error message
   * @returns Structured SyncError
   */
  private createSyncError(
    error: unknown,
    fallbackCode: SyncErrorCode,
    message: string
  ): SyncError {
    let code: SyncErrorCode = fallbackCode;

    // Map native error names to sync error codes
    if (error instanceof Error) {
      switch (error.name) {
        case 'AbortError':
          code = 'PERMISSION_DENIED';
          break;
        case 'NotFoundError':
          code = 'FILE_NOT_FOUND';
          break;
        case 'SecurityError':
        case 'NotAllowedError':
          code = 'PERMISSION_DENIED';
          break;
        case 'QuotaExceededError':
          code = 'QUOTA_EXCEEDED';
          break;
        case 'TypeError':
          code = 'INVALID_FILE';
          break;
        case 'NetworkError':
          code = 'NETWORK_ERROR';
          break;
        default:
          code = fallbackCode;
      }
    }

    return {
      code,
      message,
      provider: this.name,
      details: error,
    };
  }

  /**
   * Get or create unique identifier for current directory
   *
   * Reads .app-directory-id file or creates it if it doesn't exist.
   * This allows us to track which specific directory we're synced to,
   * enabling proper conflict detection when switching between directories.
   *
   * @returns Directory UUID or null if not connected
   *
   * @example
   * ```typescript
   * const provider = new LocalDirectorySyncProvider();
   * await provider.connect();
   * const directoryId = await provider.getDirectoryId();
   * // directoryId: "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
   * ```
   */
  async getDirectoryId(): Promise<string | null> {
    if (!this.directoryHandle) {
      return null;
    }

    const DIRECTORY_ID_FILE = '.app-directory-id';

    try {
      // Try to read existing ID file
      try {
        const fileHandle = await this.directoryHandle.getFileHandle(DIRECTORY_ID_FILE);
        const file = await fileHandle.getFile();
        const text = await file.text();

        // Validate it's a UUID (36 characters with dashes)
        const uuid = text.trim();
        if (uuid && uuid.length === 36 && uuid.match(/^[0-9a-f-]{36}$/i)) {
          console.log('📁 Found existing directory ID:', uuid);
          return uuid;
        } else {
          console.warn('⚠️ Invalid directory ID found, creating new one');
        }
      } catch (_error) {
        // File doesn't exist - this is expected for new directories
        console.log('📁 No directory ID found, creating new one');
      }

      // Create new directory ID
      const newDirectoryId = generateUUID();

      const fileHandle = await this.directoryHandle.getFileHandle(
        DIRECTORY_ID_FILE,
        { create: true }
      );
      const writable = await fileHandle.createWritable();
      await writable.write(newDirectoryId);
      await writable.close();

      console.log('✅ Created directory ID:', newDirectoryId);
      return newDirectoryId;
    } catch (error) {
      console.error('❌ Failed to get/create directory ID:', error);
      return null;
    }
  }

  /**
   * Archive current directory state (baseline + deltas + manifest)
   *
   * Creates a timestamped snapshot of the complete sync state before overwriting.
   * Archives are stored in /archive/YYYY-MM-DD-HH-MM-SS/ subdirectory.
   *
   * @returns Promise resolving to archive folder name or null on failure
   *
   * @example
   * ```typescript
   * const provider = new LocalDirectorySyncProvider();
   * await provider.connect();
   * const archiveName = await provider.archiveCurrentState();
   * // archiveName: '2025-10-10-20-15-30'
   * ```
   */
  async archiveCurrentState(): Promise<string | null> {
    if (!this.directoryHandle) {
      console.error('❌ Cannot archive: No directory connected');
      return null;
    }

    try {
      // Create timestamp for archive folder name (YYYY-MM-DD-HH-MM-SS)
      const now = new Date();
      const isoString = now.toISOString();
      const timestamp = isoString
        .replace(/T/, '-')
        .replace(/:/g, '-')
        .split('.')[0] || isoString; // "2025-10-10-20-15-30"

      console.log(`📦 Creating archive: ${timestamp}`);

      // Create archive directory
      const archiveRoot = await this.directoryHandle.getDirectoryHandle('archive', { create: true });
      const archiveFolder = await archiveRoot.getDirectoryHandle(timestamp, { create: true });

      // Get all files in root directory (baseline, deltas, manifest)
      const filesToArchive: string[] = [];

      // @ts-expect-error - TypeScript doesn't know about values() iterator on FileSystemDirectoryHandle
      for await (const entry of this.directoryHandle.values()) {
        if (entry.kind === 'file' && entry.name) {
          const name: string = entry.name;
          // Archive baseline, deltas, and manifest (skip .app-directory-id)
          if (name.startsWith('baseline_') || name.startsWith('delta_') || name === 'sync_manifest.json') {
            filesToArchive.push(name);
          }
        }
      }

      // Copy each file to archive folder
      for (const fileName of filesToArchive) {
        const sourceFile = await this.directoryHandle.getFileHandle(fileName);
        const file = await sourceFile.getFile();
        const content = await file.text();

        const archiveFile = await archiveFolder.getFileHandle(fileName, { create: true });
        const writable = await archiveFile.createWritable();
        await writable.write(content);
        await writable.close();

        console.log(`  ✅ Archived: ${fileName}`);
      }

      console.log(`✅ Archive created: archive/${timestamp}/ (${filesToArchive.length} files)`);
      return timestamp;
    } catch (error) {
      console.error('❌ Failed to create archive:', error);
      return null;
    }
  }
}
